import type { OnPageTransitionStartSync } from "vike/types";
import { startSpaJobCardTransition } from '../utils/view-transitions/job-card-transition';
import { saveSrpScrollPosition } from '../utils/navigation/srp-scroll-restoration';
import { revealKeptSrpForBackNavigation } from '../utils/navigation/srp-keep-alive';

// Store navigation state in a way that can be accessed by client components
// Initialize as a simple object to avoid any potential SSR issues
const navigationState: { isNavigating: boolean } = { isNavigating: false };

export const onPageTransitionStart: OnPageTransitionStartSync = (pageContext) => {
  // Mark navigation as started - this will be picked up by NavigationContext
  // This runs on both server and client, but we only dispatch events on client
  if (typeof window === 'undefined') {
    // SSR: do nothing, just return
    return;
  }

  // Client-side only code
  try {
    // No initial-load guard here: Vike only calls this hook for client-side navigations (never on
    // hydration). The old `!window.__vikeHasNavigated` check, that flag being set at the END of a
    // navigation, made the first click after every fresh page load skip this whole hook (no
    // progress bar, no card morph, no skeleton job page).
    navigationState.isNavigating = true;

    // Export getter for client-side access (set it here to ensure it's available)
    (window as any).__vikeNavigationState = navigationState;

    // The URL is still the page being left here, so this keys the results list's scroll position
    // to the right SRP URL for Back to restore (no-op off the SRP).
    saveSrpScrollPosition();

    // Back to a search that's still kept alive (srp-keep-alive.tsx): show it now, before Vike's
    // pageContext fetch, and skip the Back morph since there's nothing to wait for or animate.
    // Otherwise this must run synchronously, before Vike swaps in the new page, so the browser's
    // "old" snapshot still has the armed SRP card (or the job page container) in it. See
    // job-card-transition.ts.
    if (!revealKeptSrpForBackNavigation(pageContext)) {
      startSpaJobCardTransition(pageContext);
    }

    // Dispatch custom event that NavigationContext can listen to
    // Use a small delay to ensure the event is dispatched after React has mounted
    setTimeout(() => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('vike:pageTransitionStart'));
      }
    }, 0);
  } catch (error) {
    // Silently fail if there's any error (e.g., during SSR or module loading)
    console.warn('[onPageTransitionStart] Error:', error);
  }
};
